import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api } from "../App";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import {
  ArrowLeft,
  Loader2,
  Ticket,
  CheckCircle2,
  Heart,
  Star,
  TrendingUp,
  Edit3,
  Scan,
  Store,
  AlertTriangle,
} from "lucide-react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  Legend,
  CartesianGrid,
} from "recharts";

function StatCard({ icon: Icon, label, value, color = "terracotta", testId }) {
  return (
    <div className="card-base p-4 flex items-center gap-3" data-testid={testId}>
      <div className={`w-11 h-11 rounded-xl flex items-center justify-center bg-${color}/10 text-${color}`}>
        <Icon className="w-5 h-5" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-xs uppercase tracking-wider text-neutral-500">{label}</p>
        <p className="text-xl font-bold text-agave">{value}</p>
      </div>
    </div>
  );
}

export default function GuiaBusinessDashboardPage() {
  const { businessId } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      try {
        const { data } = await api.get(`/guia/businesses/${businessId}/dashboard`);
        if (!cancelled) setData(data);
      } catch (err) {
        if (!cancelled) setError(err.response?.data?.detail || "Error al cargar el panel");
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [businessId]);

  if (loading) {
    return (
      <div className="min-h-screen bg-sand flex items-center justify-center" data-testid="biz-dashboard-loading">
        <Loader2 className="w-8 h-8 animate-spin text-terracotta" />
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="min-h-screen bg-sand flex items-center justify-center p-6" data-testid="biz-dashboard-error">
        <div className="card-base text-center max-w-md">
          <AlertTriangle className="w-12 h-12 text-amber-500 mx-auto mb-3" />
          <h2 className="font-heading text-xl text-agave mb-2">{error || "Negocio no encontrado"}</h2>
          <Link to="/guia" className="btn-primary inline-flex mt-2">Volver a la Guía</Link>
        </div>
      </div>
    );
  }

  const { business, stats, recent_claims, series_30d } = data;
  const chartData = (series_30d || []).map((d) => ({
    date: d.date.slice(5), // MM-DD
    Reclamados: d.claims,
    Canjeados: d.redemptions,
  }));

  return (
    <div className="min-h-screen bg-sand pb-12" data-testid="biz-dashboard-page">
      <div className="container-main py-6">
        <Link
          to="/guia"
          className="inline-flex items-center gap-2 text-sm text-neutral-600 hover:text-agave mb-4"
          data-testid="biz-dashboard-back"
        >
          <ArrowLeft className="w-4 h-4" />
          Volver a la Guía
        </Link>

        {/* Header */}
        <div className="card-base mb-6 flex items-center gap-4 flex-wrap">
          {business.logo_url ? (
            <img src={business.logo_url} alt="" className="w-16 h-16 rounded-2xl object-cover" />
          ) : (
            <div className="w-16 h-16 rounded-2xl bg-neutral-200 flex items-center justify-center">
              <Store className="w-7 h-7 text-neutral-500" />
            </div>
          )}
          <div className="flex-1 min-w-0">
            <h1 className="text-2xl font-heading font-bold text-agave truncate">{business.name}</h1>
            <p className="text-sm text-neutral-600">
              {business.city} · {business.category}
            </p>
            {business.status !== "approved" && (
              <Badge className="bg-amber-100 text-amber-800 border-amber-200 mt-2">
                Estado: {business.status}
              </Badge>
            )}
          </div>
          <div className="flex gap-2 flex-wrap">
            <Link
              to={`/guia/${business.business_id}`}
              className="btn-secondary inline-flex items-center gap-1"
              data-testid="biz-dashboard-view-public"
            >
              Ver público
            </Link>
            <Link
              to={`/guia/redeem`}
              className="btn-primary inline-flex items-center gap-1"
              data-testid="biz-dashboard-redeem-link"
            >
              <Scan className="w-4 h-4" />
              Canjear cupón
            </Link>
          </div>
        </div>

        {/* Stats grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <StatCard
            icon={Ticket}
            label="Cupones reclamados"
            value={stats.claims}
            color="terracotta"
            testId="stat-claims"
          />
          <StatCard
            icon={CheckCircle2}
            label="Canjeados"
            value={stats.redemptions}
            color="emerald-600"
            testId="stat-redemptions"
          />
          <StatCard
            icon={TrendingUp}
            label="Conversión"
            value={`${stats.conversion_pct}%`}
            color="blue-600"
            testId="stat-conversion"
          />
          <StatCard
            icon={Heart}
            label="Favoritos"
            value={stats.favorites}
            color="pink-500"
            testId="stat-favorites"
          />
        </div>

        {/* Rating + reviews */}
        <div className="card-base mb-6 flex items-center justify-between flex-wrap gap-3" data-testid="stat-rating">
          <div className="flex items-center gap-3">
            <Star className="w-7 h-7 text-amber-400 fill-amber-400" />
            <div>
              <p className="text-xs uppercase text-neutral-500">Calificación promedio</p>
              <p className="text-xl font-bold text-agave">
                {stats.rating_avg ? `${stats.rating_avg.toFixed(1)} / 5` : "Sin reseñas"}
              </p>
              <p className="text-xs text-neutral-500">{stats.rating_count} reseña(s)</p>
            </div>
          </div>
          <Link
            to={`/guia/${business.business_id}#reviews`}
            className="text-sm text-terracotta font-medium hover:underline"
          >
            Ver reseñas →
          </Link>
        </div>

        {/* 30-day chart */}
        <div className="card-base mb-6" data-testid="dashboard-chart">
          <h2 className="font-heading font-semibold text-agave mb-3">Últimos 30 días</h2>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={chartData} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e5e5" />
                <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
                <Tooltip />
                <Legend />
                <Line type="monotone" dataKey="Reclamados" stroke="#8B3A2B" strokeWidth={2} />
                <Line type="monotone" dataKey="Canjeados" stroke="#16a34a" strokeWidth={2} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Recent claims */}
        <div className="card-base" data-testid="recent-claims">
          <h2 className="font-heading font-semibold text-agave mb-3">Actividad reciente</h2>
          {recent_claims.length === 0 ? (
            <p className="text-sm text-neutral-500 py-6 text-center">Aún no hay cupones reclamados.</p>
          ) : (
            <div className="divide-y divide-neutral-100">
              {recent_claims.map((c) => (
                <div
                  key={c.coupon_id}
                  className="py-3 flex items-center justify-between gap-3"
                  data-testid={`recent-claim-${c.coupon_id}`}
                >
                  <div className="flex-1 min-w-0">
                    <p className="font-mono font-semibold text-sm tracking-wider">{c.code}</p>
                    <p className="text-xs text-neutral-500">
                      {new Date(c.claimed_at).toLocaleString("es-MX", {
                        month: "short",
                        day: "numeric",
                        hour: "2-digit",
                        minute: "2-digit",
                      })}
                    </p>
                  </div>
                  {c.status === "redeemed" ? (
                    <Badge className="bg-emerald-600 text-white border-0 gap-1">
                      <CheckCircle2 className="w-3 h-3" />
                      Canjeado
                    </Badge>
                  ) : c.status === "expired" ? (
                    <Badge className="bg-neutral-400 text-white border-0">Expirado</Badge>
                  ) : (
                    <Badge className="bg-amber-500 text-white border-0">Activo</Badge>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
